import { Box, Button, IconButton, Typography, useMediaQuery } from "@mui/material";
import { useDispatch, useSelector } from "react-redux";
import { bloqcibeApi, useExternalVerifyCRFMutation, useGetSubjectDetailQuery, useGetTrialSiteInfoQuery } from "../../../store/slices/apiSlice";
import { findObjectByKey, getUserRole } from "../../util";
import { forwardRef, useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useLocation, useNavigate, useParams, useSearchParams } from "react-router-dom";
import { base64ToArrayBuffer } from "../../../components/common/DocumentUpload";
import _ from 'lodash';
import { useSpring, animated } from '@react-spring/web';
import SvgIcon from '@mui/material/SvgIcon';
import RightArrow from "../../../components/icons/RightArrow";
import Collapse from '@mui/material/Collapse';
import { alpha, styled, useTheme } from '@mui/material/styles';
import { TreeView } from '@mui/x-tree-view/TreeView';
import { TreeItem, treeItemClasses } from '@mui/x-tree-view/TreeItem';
import { SKIPPED_FIELDS_KEY } from "../../../util/constants";
import FormDetails from "../subjectDetailCRF/FormDetails";
import { mergeCustomFields, getNestApiErrorMessage } from "../../../util/util";
import { openMessage } from "../../../store/slices/showMessageSlice";
import PendingActionsRoundedIcon from '@mui/icons-material/PendingActionsRounded';
import SubjectParticipantIdHeading from "../../../components/common/SubjectParticipantIdHeading";
import { setUserLocation } from "../../../store/slices/userDetailsSlice";
import {
  setQueries,
  setQueriesLoading,
  setQueriesError,
  clearQueries,
  selectHasOpenQueries,
  selectQueriesByFieldKey,
  selectRefetchTrigger,
} from "../../../store/slices/querySlice";

function MinusSquare(props) {
  return (
    <SvgIcon fontSize="inherit" style={{ width: 14, height: 14, color: '#FF6602' }} {...props}>
      {/* tslint:disable-next-line: max-line-length */}
      <path d="M22.047 22.074v0 0-20.147 0h-20.12v0 20.147 0h20.12zM22.047 24h-20.12q-.803 0-1.365-.562t-.562-1.365v-20.147q0-.776.562-1.351t1.365-.575h20.147q.776 0 1.351.575t.575 1.351v20.147q0 .803-.575 1.365t-1.378.562v0zM17.873 11.023h-11.826q-.375 0-.669.281t-.294.682v0q0 .401.294 .682t.669.281h11.826q.375 0 .669-.281t.294-.682v0q0-.401-.294-.682t-.669-.281z" />
    </SvgIcon>
  );
}
function PlusSquare(props) {
  return (
    <SvgIcon fontSize="inherit" style={{ width: 14, height: 14, color: '#FF6602' }} {...props}>
      {/* tslint:disable-next-line: max-line-length */}
      <path d="M22.047 22.074v0 0-20.147 0h-20.12v0 20.147 0h20.12zM22.047 24h-20.12q-.803 0-1.365-.562t-.562-1.365v-20.147q0-.776.562-1.351t1.365-.575h20.147q.776 0 1.351.575t.575 1.351v20.147q0 .803-.575 1.365t-1.378.562v0zM17.873 12.977h-4.923v4.896q0 .401-.281.682t-.682.281v0q-.375 0-.669-.281t-.294-.682v-4.896h-4.923q-.401 0-.682-.294t-.281-.669v0q0-.401.281-.682t.682-.281h4.923v-4.896q0-.401.294-.682t.669-.281v0q.401 0 .682.281t.281.682v4.896h4.923q.401 0 .682.281t.281.682v0q0 .375-.281.669t-.682.294z" />
    </SvgIcon>
  );
}

function TransitionComponent(props) {
  const style = useSpring({
    to: {
      opacity: props.in ? 1 : 0,
      transform: `translate3d(${props.in ? 0 : 20}px,0,0)`,
    },
  });

  return (
    <animated.div style={style}>
      <Collapse {...props} />
    </animated.div>
  );
}
const CustomTreeItem = forwardRef((props, ref) => (
  <TreeItem {...props} TransitionComponent={TransitionComponent} ref={ref} />
));

const StyledTreeItem = styled(CustomTreeItem)(({ theme }) => ({
  [`& .${treeItemClasses.iconContainer}`]: {
    '& .close': {
      opacity: 0.3,
    },
  },
  [`& .${treeItemClasses.group}`]: {
    marginLeft: 15,
    paddingLeft: 18,
    borderLeft: `1px dashed ${alpha(theme.palette.text.primary, 0.4)}`,
  },
}));

function isVisitSectionSelectable(section, sectionSatus) {
  if (sectionSatus?.status === 'Pending') return false;
  if (!(section.showInReadOnly == undefined || section.showInReadOnly)) return false;
  return true;
}

function isVisitSubTabSelectable(subTab, sectionSatus) {
  if (SKIPPED_FIELDS_KEY.includes(subTab.key)) return false;
  const subSectionSatus = sectionSatus?.subSectionStatuses?.find((a) => a.subSectionKey === subTab.key);
  if (subSectionSatus?.status === 'Pending') return false;
  if (!(subTab.showInReadOnly == undefined || subTab.showInReadOnly)) return false;
  return true;
}

const SubjectVisitDetailCRFInfo = (props) => {
  const { state } = useLocation();
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const [, setOriginalFormConfig] = useState(null);
  const [mergedVisitFormConfig, setMergedVisitFormConfig] = useState(null);
  const { trialId, siteId, trialSiteId, subjectMasterId, crfMasterId, visitStepKey } = useParams();
  const crfDocumentKey = useSelector((state) => state?.trial?.trialSiteDetail?.siteTrialData?.trial?.crfForm);
  const enrolmentDocumentKey = useSelector((state) => state?.trial?.trialSiteDetail?.siteTrialData?.trial?.eConsentForm);
  const sponsorId = useSelector((state) => state.auth.sponsorId);
  const { user: loggedInUser, userData } = useSelector((state) => state.auth);
  const [getLibraryForm] = bloqcibeApi.endpoints.getLibraryForm.useLazyQuery();
  const [selectedForm, setSelectedForm] = useState();
  const [selectedSectionKey, setSelectedSectionKey] = useState();
  const [selectedSubSectionKey, setSelectedSubSectionKey] = useState();
  const [manualSelection, setManualSelection] = useState(false);
  const [selectedfield, setSelectedField] = useState(null);
  const [formConfigLoaded, setFormConfigLoaded] = useState(false);
  const [expandedNodes, setExpandedNodes] = useState([]);
  const [selectedNodeId, setSelectedNodeId] = useState(null);
  const skipDefaultUrlSync = useRef(false);
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));

  //START USING V2 API
  const [externalVerifyCRF] = useExternalVerifyCRFMutation()
  const [downloadCRFFile] = bloqcibeApi.endpoints.downloadCRFFile.useLazyQuery();
  const { locationLat, locationLng } = useSelector((state) => state.userDetails);
  const [formConfig, setFormConfig] = useState(null);
  // const [formConfig, setFormConfig] = useState(originalConfig.steps.find((_obj) => _obj.key === visitStepKey));
  // const [userSiteTrialRole, setUserSiteTrialRole] = useState();
  const [downloadVisitSignature] =
    bloqcibeApi.endpoints.downloadVisitSignature.useLazyQuery();
  const [getEConsentFormConfig] = bloqcibeApi.endpoints.getEConsentFormConfig.useLazyQuery();
  const { data: subjectDetailWithSteps } = useGetSubjectDetailQuery(subjectMasterId);
  const [getVisitAnswers, { data: visitDetails }] = bloqcibeApi.endpoints.getVisitAnswers.useLazyQuery();
  const [getQueries] = bloqcibeApi.endpoints.getQueries.useLazyQuery();
  const hasOpenQueries = useSelector(selectHasOpenQueries);
  const queriesByFieldKey = useSelector(selectQueriesByFieldKey);
  const refetchTrigger = useSelector(selectRefetchTrigger);
  const crfStepStatus = useMemo(() => {
    if (subjectDetailWithSteps) {
      const steps = _.sortBy(subjectDetailWithSteps?.crfDetail?.stepStatus, 'order');
      return steps;
    }
    return [];
  }, [subjectDetailWithSteps]);

  const { data: trialSiteData } = useGetTrialSiteInfoQuery({
    sponsorId: sponsorId,
    trialId: trialId,
    siteTrialId: trialSiteId,
  });

  const userSiteTrialRole = useMemo(() => {
    return getUserRole(loggedInUser, trialId, trialSiteData?.siteTrialData?.site?.id, userData);
  }, [loggedInUser, trialSiteData, userData]);

  const visitStepStatusRow = useMemo(() => {
    if (!subjectDetailWithSteps?.crfDetail?.stepStatus) return null;
    return subjectDetailWithSteps.crfDetail.stepStatus.find((_step) => _step.stepKey === visitStepKey);
  }, [subjectDetailWithSteps, visitStepKey]);

  useEffect(() => {
    skipDefaultUrlSync.current = false;
  }, [visitStepKey, crfMasterId, subjectMasterId]);

  useEffect(() => {
    (async () => {
      setSelectedForm(null);
      setFormConfigLoaded(false);
      setMergedVisitFormConfig(null);
      if (crfDocumentKey) {
        const _formDetailsData = await getLibraryForm(
          `${process.env.REACT_APP_API_ENDPOINT_URL}form-library/library/bloqcube/${crfDocumentKey}`
        );
        if (_formDetailsData.data) {
          setOriginalFormConfig(_formDetailsData.data);
        } else if (_formDetailsData.error) {
          console.error('UNABLE TO GET FORM DETAILS');
        }
        if (trialSiteData) {
          const formDetailsData = await getEConsentFormConfig({
            sponsorId,
            trialId,
            siteId: trialSiteData.siteTrialData.siteId,
            documentKey: crfDocumentKey,
            payload: {
              subjectMasterId: subjectMasterId,
            },
          });
          if (formDetailsData.data) {
            const _tempConfig = { ..._formDetailsData.data };
            const mergedConfig = mergeCustomFields(_tempConfig, formDetailsData.data);
            setMergedVisitFormConfig(mergedConfig);
            setFormConfig(mergedConfig.steps.find((_obj) => _obj.key === visitStepKey));
          } else if (formDetailsData.error) {
            console.error('UNABLE TO GET FORM DETAILS');
            if (_formDetailsData.data) {
              setMergedVisitFormConfig(_formDetailsData.data);
              setFormConfig(_formDetailsData.data.steps.find((_obj) => _obj.key === visitStepKey));
            }
          } else {
            setMergedVisitFormConfig(_formDetailsData.data);
            setFormConfig(_formDetailsData.data.steps.find((_obj) => _obj.key === visitStepKey));
          }

          await getVisitAnswers({
            sponsorId: sponsorId,
            trialId: trialId,
            siteId: trialSiteData?.siteTrialData?.site?.id,
            payload: {
              crfMasterId: crfMasterId,
              subjectMasterId: subjectMasterId,
              stepKey: visitStepKey,
            },
          });
        }
        setFormConfigLoaded(true);
      } else {
        setFormConfigLoaded(true);
      }
    })();
  }, [trialSiteData, visitStepKey, crfDocumentKey]);

  useEffect(() => {
    if (!formConfig || !formConfigLoaded || manualSelection) return;

    const querySectionKey = searchParams.get('sectionKey');
    const querySubSectionKey = searchParams.get('subSectionKey');
    const queryFieldKey = searchParams.get('fieldKey');

    const applyKeys = (sectionKey, subSectionKey, fieldKeyOpt) => {
      const section = formConfig.sections?.find((s) => s.key === sectionKey);
      if (!section) return false;
      const sectionSatus = visitStepStatusRow?.sectionStatuses?.find((a) => a.sectionKey === section.key);
      if (!isVisitSectionSelectable(section, sectionSatus)) return false;

      if (section.subTabs && section.subTabs.length) {
        let subTab = null;
        if (subSectionKey) {
          subTab = section.subTabs.find((st) => st.key === subSectionKey);
          if (!subTab || !isVisitSubTabSelectable(subTab, sectionSatus)) return false;
        } else {
          for (const st of section.subTabs) {
            if (!isVisitSubTabSelectable(st, sectionSatus)) continue;
            subTab = st;
            break;
          }
          if (!subTab) return false;
        }
        setSelectedSectionKey(section.key);
        setSelectedSubSectionKey(subTab.key);
        setSelectedForm(subTab);
        setExpandedNodes([section.key]);
        setSelectedNodeId(`${section.key}@${subTab.key}`);
      } else {
        setSelectedSectionKey(section.key);
        setSelectedSubSectionKey(undefined);
        setSelectedForm(section);
        setExpandedNodes([section.key]);
        setSelectedNodeId(section.key);
      }
      if (fieldKeyOpt) setSelectedField(fieldKeyOpt);
      else setSelectedField(null);
      return true;
    };

    if (querySectionKey) {
      applyKeys(querySectionKey, querySubSectionKey || undefined, queryFieldKey || undefined);
      return;
    }

    if (skipDefaultUrlSync.current) return;

    const stateSectionKey = state?.sectionKey;
    const stateSubSectionKey = state?.subSectionKey;
    const stateFieldKey = state?.fieldKey;
    const stateCategoryKey = state?.categoryKey;

    if (stateSectionKey && applyKeys(stateSectionKey, stateSubSectionKey, stateFieldKey)) {
      const params = new URLSearchParams();
      params.set('sectionKey', stateSectionKey);
      if (stateSubSectionKey) params.set('subSectionKey', stateSubSectionKey);
      if (stateFieldKey) params.set('fieldKey', stateFieldKey);
      if (stateCategoryKey) params.set('categoryKey', stateCategoryKey);
      setSearchParams(params, { replace: true });
      return;
    }

    for (const section of formConfig.sections || []) {
      const sectionSatus = visitStepStatusRow?.sectionStatuses?.find((a) => a.sectionKey === section.key);
      if (!isVisitSectionSelectable(section, sectionSatus)) continue;

      if (section.subTabs && section.subTabs.length) {
        for (const st of section.subTabs) {
          if (!isVisitSubTabSelectable(st, sectionSatus)) continue;
          setSelectedSectionKey(section.key);
          setSelectedSubSectionKey(st.key);
          setSelectedForm(st);
          setExpandedNodes([section.key]);
          setSelectedNodeId(`${section.key}@${st.key}`);
          if (stateFieldKey) setSelectedField(stateFieldKey);
          else setSelectedField(null);
          const params = new URLSearchParams();
          params.set('sectionKey', section.key);
          params.set('subSectionKey', st.key);
          if (stateFieldKey) params.set('fieldKey', stateFieldKey);
          if (stateCategoryKey) params.set('categoryKey', stateCategoryKey);
          setSearchParams(params, { replace: true });
          return;
        }
      } else {
        setSelectedSectionKey(section.key);
        setSelectedSubSectionKey(undefined);
        setSelectedForm(section);
        setExpandedNodes([section.key]);
        setSelectedNodeId(section.key);
        if (stateFieldKey) setSelectedField(stateFieldKey);
        else setSelectedField(null);
        const params = new URLSearchParams();
        params.set('sectionKey', section.key);
        if (stateFieldKey) params.set('fieldKey', stateFieldKey);
        if (stateCategoryKey) params.set('categoryKey', stateCategoryKey);
        setSearchParams(params, { replace: true });
        return;
      }
    }
  }, [
    formConfig,
    formConfigLoaded,
    manualSelection,
    searchParams,
    setSearchParams,
    state,
    visitStepStatusRow,
  ]);

  useEffect(() => {
    const fetchQueries = async () => {
      if (
        !sponsorId ||
        !trialId ||
        !siteId ||
        !subjectMasterId ||
        !visitStepKey ||
        !selectedSectionKey ||
        !crfMasterId
      ) {
        return;
      }

      dispatch(setQueriesLoading(true));
      try {
        const result = await getQueries({
          sponsorId,
          trialId,
          siteId,
          stepKey: visitStepKey,
          sectionKey: selectedSectionKey,
          subSectionKey: selectedSubSectionKey,
          subjectMasterId,
          crfMasterId,
          page: 1,
          limit: 100,
          active: true,
        });

        if (result.data) {
          const fetchedQueries = result.data.data || [];
          dispatch(setQueries(fetchedQueries));
        } else {
          dispatch(setQueries([]));
          if (result.error) {
            const message = getNestApiErrorMessage(result.error, 'Failed to load queries');
            dispatch(setQueriesError(message));
            dispatch(openMessage({ message, messageSeverity: 'error' }));
          }
        }
      } catch (error) {
        console.error('Error fetching queries:', error);
        dispatch(setQueries([]));
        const message = getNestApiErrorMessage(error, 'Failed to load queries');
        dispatch(setQueriesError(message));
        dispatch(openMessage({ message, messageSeverity: 'error' }));
      }
    };

    fetchQueries();
  }, [
    sponsorId,
    trialId,
    siteId,
    subjectMasterId,
    visitStepKey,
    selectedSectionKey,
    selectedSubSectionKey,
    crfMasterId,
    dispatch,
    getQueries,
    refetchTrigger,
  ]);

  useEffect(() => {
    return () => {
      dispatch(clearQueries());
    };
  }, [dispatch]);

  const handleDownloadSignature = useCallback(async (s3Key, setSign) => {
    try {
      const result = await downloadVisitSignature({
        s3Key: s3Key,
      });
      if (result.data) {
        const byteArray = base64ToArrayBuffer(result.data);
        const rr = new Blob([byteArray], { type: "application/octet-stream" });
        const reader = new FileReader();
        reader.readAsDataURL(rr);
        reader.onloadend = function () {
          const base64data = reader.result;
          setSign(base64data);
        };
        reader.onerror = function () {
          console.log("Failed to read signature data");
          setSign(null);
        };
      } else {
        console.log("Failed to load signature");
        setSign(null);
      }
    } catch (error) {
      console.error("Error downloading signature:", error);
      setSign(null);
    }
  }, [downloadVisitSignature]);
  const openStepForm = (stepKey) => {
    navigate(`/eConcent/${trialId}/trial-site/${trialSiteId}/${visitStepKey}/${subjectMasterId}/crf/${crfMasterId}`)
  };
  function findObjectByValue(
    obj,
    targetValue,
    path = [],
  ) {
    for (const key in obj) {
      if (obj[key] === targetValue) {
        return obj;
      } else if (typeof obj[key] === "object" && obj[key] !== null) {
        // Continue searching in the nested object
        const result = findObjectByValue(
          obj[key],
          targetValue,
          [...path, key],
        );
        if (result) {
          return result;
        }
      }
    }
    // Value not found in the current object
    return null;
  }
  const onFormSelect = (e, node) => {
    const _sectionKey = node.split('@')[0];
    const _subSectionKey = node.split('@')[1];
    const _sec = findObjectByValue(formConfig, _subSectionKey ? _subSectionKey : _sectionKey);
    if (_sec && !_sec.subTabs) {
      setManualSelection(true);
      skipDefaultUrlSync.current = false;
      setSelectedSectionKey(_sectionKey);
      setSelectedSubSectionKey(_subSectionKey);
      setSelectedForm(_sec);
      const params = new URLSearchParams(searchParams);
      params.set('sectionKey', _sectionKey);
      if (_subSectionKey) {
        params.set('subSectionKey', _subSectionKey);
      } else {
        params.delete('subSectionKey');
      }
      params.delete('fieldKey');
      params.delete('categoryKey');
      params.delete('tab');
      setSearchParams(params, { replace: true });
      if (_subSectionKey) {
        setExpandedNodes([_sectionKey]);
        setSelectedNodeId(`${_sectionKey}@${_subSectionKey}`);
      } else {
        setExpandedNodes([_sectionKey]);
        setSelectedNodeId(_sectionKey);
      }
    }
  };

  const handleBackToTree = () => {
    setSelectedForm(null);
    setSelectedField(null);
    setSelectedSectionKey(undefined);
    setSelectedSubSectionKey(undefined);
    setManualSelection(false);
    skipDefaultUrlSync.current = true;
    dispatch(clearQueries());
    const params = new URLSearchParams(searchParams);
    params.delete('sectionKey');
    params.delete('subSectionKey');
    params.delete('fieldKey');
    params.delete('categoryKey');
    params.delete('tab');
    setSearchParams(params, { replace: true });
    setExpandedNodes([]);
    setSelectedNodeId(null);
  };
  const editAllowed = useMemo(() => {
    const stepStatusData = crfStepStatus.find((_obj) => _obj.stepKey === visitStepKey);

    if (
      stepStatusData &&
      ['Withdrawal', 'Enrollment_Failure', 'Screening_Failure', 'On_Hold'].includes(stepStatusData.status)
    ) {
      return false;
    }

    if (stepStatusData && stepStatusData.userAccess) {
      const userAccess = JSON.parse(stepStatusData.userAccess);
      const editAccess = userAccess.edit;
      const isAccess =
        typeof editAccess !== 'string'
          ? _.find(editAccess, (access) => {
              return access === userSiteTrialRole;
            })
          : editAccess === userSiteTrialRole;

      if (isAccess) {
        return true;
      }

      const readOnlyEditConfig = formConfig?.readOnlyEdit;
      if (readOnlyEditConfig) {
        const currentStatus = stepStatusData.status;

        if (readOnlyEditConfig.statusAccess && readOnlyEditConfig.statusAccess[currentStatus]) {
          const statusRoles = readOnlyEditConfig.statusAccess[currentStatus];
          const hasStatusAccess = Array.isArray(statusRoles)
            ? statusRoles.includes(userSiteTrialRole)
            : statusRoles === userSiteTrialRole;

          if (hasStatusAccess) {
            return true;
          }
        }

        const allowApplicableStatus = readOnlyEditConfig.allowApplicableStatus || [];
        const readOnlyEditRoles = readOnlyEditConfig.roles || readOnlyEditConfig.access?.edit || [];

        if (allowApplicableStatus.length > 0) {
          const isStatusAllowed = allowApplicableStatus.includes(currentStatus);
          const hasReadOnlyEditAccess = Array.isArray(readOnlyEditRoles)
            ? readOnlyEditRoles.includes(userSiteTrialRole)
            : readOnlyEditRoles === userSiteTrialRole;

          if (isStatusAllowed && hasReadOnlyEditAccess) {
            return true;
          }
        }
      }

      if (hasOpenQueries) {
        const currentStatus = stepStatusData.status;
        const verifyAccess = userAccess.verify;
        const hasVerifyAccess = _.find(verifyAccess, (access) => access === userSiteTrialRole);



        if (hasVerifyAccess) {
          return true;
        }

        if (readOnlyEditConfig) {
          if (readOnlyEditConfig.queryResolutionAccess) {
            const queryRoles = readOnlyEditConfig.queryResolutionAccess;
            const hasQueryAccess = Array.isArray(queryRoles)
              ? queryRoles.includes(userSiteTrialRole)
              : queryRoles === userSiteTrialRole;

            if (hasQueryAccess) {
              return true;
            }
          }

          if (readOnlyEditConfig.statusAccess && readOnlyEditConfig.statusAccess[currentStatus]) {
            const statusRoles = readOnlyEditConfig.statusAccess[currentStatus];
            const hasStatusAccess = Array.isArray(statusRoles)
              ? statusRoles.includes(userSiteTrialRole)
              : statusRoles === userSiteTrialRole;

            if (hasStatusAccess) {
              return true;
            }
          }

          const readOnlyEditRoles = readOnlyEditConfig.roles || readOnlyEditConfig.access?.edit || [];
          const hasReadOnlyEditAccess = Array.isArray(readOnlyEditRoles)
            ? readOnlyEditRoles.includes(userSiteTrialRole)
            : readOnlyEditRoles === userSiteTrialRole;

          if (hasReadOnlyEditAccess) {
            return true;
          }
        }
      }

      return false;
    }
    return false;
  }, [
    crfStepStatus,
    userSiteTrialRole,
    visitStepKey,
    hasOpenQueries,
    formConfig,
    subjectDetailWithSteps,
  ]);

  const stepSatus = useMemo(() => {
    if (subjectDetailWithSteps) {
      const stepStatusData = subjectDetailWithSteps?.crfDetail?.stepStatus.find((_obj) => _obj.stepKey === visitStepKey);
      return { status: stepStatusData.status, title: stepStatusData.stepLabel };
    }
    return { status: '', title: '' }
  }, [subjectDetailWithSteps, visitStepKey]);
  const handleDownloadFile = async (s3Key, fileName) => {
    const result = await downloadCRFFile({
      s3Key: s3Key,
    });
    if (result.data) {
      let byteArray = base64ToArrayBuffer(result.data);
      let a = window.document.createElement("a");
      a.href = window.URL.createObjectURL(
        new Blob([byteArray], { type: "application/octet-stream" })
      );
      a.download = fileName;
      document.body.appendChild(a);
      a.click();
    } else {
      console.log("Failed to load file");
    }
  }

  const readOnlyActions = useMemo(() => {
    if (formConfig) {
      return formConfig?.readOnlyActions
    }
    return null
  }, [formConfig])

  const selectedFormStatus = useMemo(() => {
    if (subjectDetailWithSteps && selectedForm) {
      const steps = _.find(subjectDetailWithSteps?.crfDetail?.stepStatus, (_step) => _step.stepKey === visitStepKey);
      const statusData = findObjectByValue(steps, selectedForm.key);
      return statusData?.status;
    }
    return null;
  }, [subjectDetailWithSteps, selectedForm, visitStepKey]);

  const getGeoLocation = async () => {
    if ('geolocation' in navigator) {
      return new Promise((resolve, reject) => {
        navigator.geolocation.getCurrentPosition(
          (position) => {
            const { latitude, longitude } = position.coords;
            console.log(`Latitude: ${latitude}, Longitude: ${longitude}`);
            if (latitude && longitude) {
              dispatch(setUserLocation({ locationLatitude: latitude, locationLongitude: longitude }));
              resolve({ latitude, longitude });
            } else {
              resolve({ latitude: 0.0, longitude: 0.0 });
            }
          },
          (error) => {
            dispatch(
              openMessage({
                message: 'Please enable location access to submit your response.',
                messageSeverity: 'error',
              })
            );
            console.error(`Error getting geolocation: ${error.message}`);
            reject(error);
          }
        );
      });
    } else {
      dispatch(openMessage({ message: 'Geolocation is not supported!', messageSeverity: 'error' }));
      console.log('Geolocation is not supported');
      return { latitude: 0.0, longitude: 0.0 };
    }
  };

  const handleSubjectVerification = async (payload) => {
    let _ans = {}
    if (selectedForm) {
      selectedForm?.categories &&
        selectedForm?.categories.forEach((category) => {
          const _answers = findObjectByKey(payload?.answers, category.key);
          if (_answers) _ans[category.key] = _answers;
        });
      selectedForm?.fields &&
        selectedForm?.fields.forEach((field) => {
          const _answer = findObjectByKey(payload?.answers, field.key);
          if (_answer) _ans[field.key] = _answer;
        });
    }
    if (!_ans.locationLongitude || !_ans.locationLatitude) {
      let locationLongitude;
      let locationLatitude;
      if (!locationLat || !locationLng) {
        const co_ords = await getGeoLocation();
        const { latitude, longitude } = co_ords;
        locationLongitude = longitude;
        locationLatitude = latitude;
      } else {
        locationLongitude = locationLng;
        locationLatitude = locationLat;
      }
      _ans.locationLongitude = locationLongitude;
      _ans.locationLatitude = locationLatitude;
    }
    const verify = await externalVerifyCRF({
      sponsorId,
      trialId,
      siteId,
      payload: {
        crfMasterId: crfMasterId,
        subjectMasterId: subjectMasterId,
        documentKey: enrolmentDocumentKey,
        documentVersion: '1.0',
        crfDocumentKey: crfDocumentKey,
        crfDocumentVersion: '1.0',
        stepKey: payload.stepKey,
        sectionKey: payload.sectionKey,
        subSectionKey: payload.subSectionKey,
        answers: _ans,
      },
    });
    if (verify.data) {
      dispatch(
        openMessage({
          message: "Verified Sucessfully!",
          messageSeverity: "success",
        })
      );
    } else if (
      verify?.error?.data?.message?.includes('External verification is not allowed because there are queries for')
    ) {
      dispatch(
        openMessage({
          message:
            'Verification is not allowed because there are queries for this form that are not in VERIFIED state. Please verify all queries and try again.',
          messageSeverity: 'error',
        })
      );
    } else {
      dispatch(
        openMessage({
          message: getNestApiErrorMessage(verify.error, 'Verification request failed!'),
          messageSeverity: 'error',
        })
      );
    }
  }
  if (
    stepSatus.status != 'Completed' &&
    stepSatus.status != 'External_Verification_Pending' &&
    stepSatus.status != 'Verification_Pending'
  ) {
    return (<Box sx={{ display: 'flex', flexDirection: 'column', rowGap: 3, justifyContent: 'center', alignItems: 'center', height: '70vh' }}>
      <PendingActionsRoundedIcon sx={{ height: 100, width: 100, color: '#FF9933' }} />
      <Typography variant="h6" color={'grey'}>{`${stepSatus.title} is pending`}</Typography>
    </Box>)
  }
  return (
    <Box>
      <Box sx={{ position: 'relative', display: 'flex', width: '100%', justifyContent: 'space-between', alignItems: 'center', pb: 2 }}>
        <Box sx={{ minWidth: 0 }}>
          {selectedForm && <>
            {isMobile ? (
              <IconButton onClick={() => navigate(-1)} aria-label="Back" sx={{ ml: -1, flexShrink: 0 }}>
                <RightArrow leftArrow />
              </IconButton>
            ) : (
              <Button type="text" onClick={() => navigate(-1)} startIcon={<RightArrow leftArrow />} sx={{ flexShrink: 0 }}>
                <Typography variant="subtitle1" sx={{ textTransform: 'none' }}>
                Back
              </Typography>
            </Button>
          )}
          </>}
        </Box>
        <SubjectParticipantIdHeading />
        <Box sx={{ minWidth: 0 }} />
      </Box>
      <Box sx={{ display: "flex", width: "100%" }}>
        {!selectedForm && <Box
          sx={{
            width: "100%",
            paddingTop: 2,
            paddingX: 2,
            border: "1px solid #E7E7E7",
            position: "sticky",
            top: "auto",
          }}
        >
          <TreeView
            expanded={expandedNodes}
            selected={selectedNodeId}
            onNodeToggle={(event, nodeIds) => {
              setExpandedNodes(nodeIds);
            }}
            defaultCollapseIcon={<MinusSquare />}
            defaultExpandIcon={<PlusSquare />}
            onNodeSelect={onFormSelect}
            sx={{ overflowX: "hidden" }}
          >
            {formConfig &&
              formConfig.sections.map((section, subIndex) => {
                let sectionSatus = visitStepStatusRow?.sectionStatuses?.find(
                  (a) => a.sectionKey === section.key
                );
                return (
                  sectionSatus?.status !== "Pending" &&
                  (section.showInReadOnly == undefined ||
                    section.showInReadOnly) && (
                    <StyledTreeItem
                      key={`${section.key}`}
                      nodeId={`${section.key}`}
                      selected={selectedNodeId === section.key}
                      sx={{
                        "& .MuiTreeItem-content": {
                          paddingY: 1,
                        },
                      }}
                      label={
                        <Box
                          sx={{
                            display: "flex",
                            alignItems: "center",
                            p: 0.5,
                            pr: 0,
                          }}
                        >
                          <Typography
                            variant="subtitle1"
                            sx={{ fontWeight: "inherit", flexGrow: 1 }}
                          >
                            {section.label}
                          </Typography>
                          {/* {sectionSatus?.status ===
                              "External_Verification_Pending" && (
                              <PendingActionsRoundedIcon
                                sx={{
                                  color: theme.palette.primary.light,
                                  fontSize: 23,
                                }}
                              />
                            )} */}
                        </Box>
                      }
                    >
                      {section &&
                        section.subTabs &&
                        section.subTabs.map((subTab, subIndex1) => {
                          let subSectionSatus =
                            sectionSatus?.subSectionStatuses?.find(
                              (a) => a.subSectionKey === subTab.key
                            );
                          return (
                            !SKIPPED_FIELDS_KEY.includes(subTab.key) &&
                            subSectionSatus?.status !== "Pending" &&
                            (subTab.showInReadOnly == undefined ||
                              subTab.showInReadOnly) && (
                              <StyledTreeItem
                                key={`${section.key}@${subTab.key}`}
                                nodeId={`${section.key}@${subTab.key}`}
                                selected={selectedNodeId === `${section.key}@${subTab.key}`}
                                label={subTab.label}
                              ></StyledTreeItem>
                            )
                          );
                        })}
                    </StyledTreeItem>
                  )
                );
              })}
          </TreeView>
        </Box>}
        {selectedForm && <Box sx={{ width: "100%", paddingX: 1 }}>
          <FormDetails
            details={selectedForm}
            handleDownload={handleDownloadFile}
            sectionKey={selectedSectionKey}
            openStepForm={openStepForm}
            stepKey={visitStepKey}
            editAllowed={editAllowed}
            subSectionKey={selectedSubSectionKey}
            formStatus={selectedFormStatus}
            handleDownloadSignature={handleDownloadSignature}
            userSiteTrialRole={userSiteTrialRole}
            readOnlyActions={readOnlyActions}
            answers={visitDetails}
            verifyAction={handleSubjectVerification}
            selectedfield={selectedfield}
            setSelectedField={setSelectedField}
            formConfig={mergedVisitFormConfig}
            subjectName={subjectDetailWithSteps?.subjectName}
            queriesByFieldKey={queriesByFieldKey}
            hasOpenQueries={hasOpenQueries}
          />
        </Box>}
      </Box>
    </Box>
  );
}

export default SubjectVisitDetailCRFInfo;